import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Webflow Entry Point for Rev.com Caption Converter
 *
 * This bundle provides a standalone Caption Converter component that can be
 * embedded in Webflow pages. It converts SRT files to various caption formats.
 *
 * Usage in Webflow:
 * 1. Add React 18 from CDN
 * 2. Add this bundle's JS and CSS files
 * 3. Call RevWebflow.initCaptionConverter() with configuration options
 *
 * Example:
 * ```html
 * <script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
 * <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
 * <link href="https://www.rev.com/build/caption-converter-webflow.css" rel="stylesheet">
 * <script src="https://www.rev.com/build/caption-converter-webflow.js"></script>
 *
 * <div id="caption-converter-container"></div>
 *
 * <script>
 *   RevWebflow.initCaptionConverter({
 *     containerId: 'caption-converter-container',
 *     theme: RevWebflowCaptionConverterThemes.dark
 *   });
 * </script>
 * ```
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { CaptionConverter } from './src/CaptionConverter';
import { DesignSystemProvider } from '@revdotcom/design-system/components/DesignSystemProvider';
// Import styles
import './styles/caption-converter.css';
/**
 * Pre-defined themes available globally
 */
export const RevWebflowThemes = {
    dark: {
        primaryColor: '#5B24FF',
        backgroundColor: '#120F20',
        textColor: 'rgba(255, 255, 255, 0.70)',
        buttonBgColor: '#5B24FF',
        buttonHoverBgColor: '#A04DFF',
        darkMode: true,
    },
    light: {
        primaryColor: '#5B24FF',
        backgroundColor: '#ffffff',
        textColor: '#333333',
        buttonBgColor: '#5B24FF',
        buttonHoverBgColor: '#A04DFF',
        darkMode: false,
    },
};
// Store root reference for cleanup
let captionConverterRoot = null;
/**
 * Apply theme CSS custom properties to the container
 */
function applyTheme(container, theme) {
    // Reset theme state when no theme provided (for re-initialization)
    if (!theme) {
        container.style.removeProperty('--cc-primary-color');
        container.style.removeProperty('--cc-background-color');
        container.style.removeProperty('--cc-text-color');
        container.style.removeProperty('--cc-button-bg-color');
        container.style.removeProperty('--cc-button-hover-bg-color');
        container.classList.remove('light-mode');
        return;
    }
    if (theme.primaryColor) {
        container.style.setProperty('--cc-primary-color', theme.primaryColor);
    }
    if (theme.backgroundColor) {
        container.style.setProperty('--cc-background-color', theme.backgroundColor);
    }
    if (theme.textColor) {
        container.style.setProperty('--cc-text-color', theme.textColor);
    }
    if (theme.buttonBgColor) {
        container.style.setProperty('--cc-button-bg-color', theme.buttonBgColor);
    }
    if (theme.buttonHoverBgColor) {
        container.style.setProperty('--cc-button-hover-bg-color', theme.buttonHoverBgColor);
    }
    // Add light mode class if dark mode is explicitly disabled
    if (theme.darkMode === false) {
        container.classList.add('light-mode');
    }
    else {
        container.classList.remove('light-mode');
    }
}
/**
 * Fetches Caption Converter configuration from the Rev.com API.
 * Always same-origin: the config endpoint is co-located on every environment, so a
 * cross-origin fetch (e.g. a lower env whose page points at prod) is CORS-blocked,
 * which fails the whole tool init.
 */
async function fetchConfig() {
    const configUrl = `${window.location.origin}/api/caption-converter/config`;
    try {
        const response = await fetch(configUrl, {
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch config: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        return {
            filestack: {
                apiKey: data.filestackApiKey,
                uploadBucket: data.uploadBucket,
                uploadPath: data.uploadPath,
                region: data.region,
                policy: data.filestackPolicy,
                signature: data.filestackSignature,
            },
            exportTypes: data.exportTypes,
        };
    }
    catch (error) {
        console.error('[RevWebflow] Error fetching config:', error);
        throw error;
    }
}
/**
 * Initialize the Caption Converter tool
 *
 * @param options - Configuration options
 * @returns Promise that resolves when the converter is mounted
 */
export async function initCaptionConverter(options) {
    const { containerId, theme } = options;
    // Find container element
    const container = document.getElementById(containerId);
    if (!container) {
        throw new Error(`[RevWebflow] Container element not found: #${containerId}`);
    }
    // Cleanup any existing instance
    if (captionConverterRoot) {
        captionConverterRoot.unmount();
        captionConverterRoot = null;
    }
    // Apply theme
    applyTheme(container, theme);
    try {
        // Fetch configuration from API (always same-origin)
        const config = await fetchConfig();
        // Create React root and render
        captionConverterRoot = createRoot(container);
        captionConverterRoot.render(_jsx(DesignSystemProvider, { scoped: true, children: _jsx(CaptionConverter, { filestackConfig: config.filestack, exportTypes: config.exportTypes, apiBaseUrl: window.location.origin }) }));
        console.log('[RevWebflow] Caption Converter initialized successfully');
    }
    catch (error) {
        console.error('[RevWebflow] Failed to initialize Caption Converter:', error);
        // Show error message in container
        container.innerHTML = `
            <div style="padding: 20px; color: #EE5A6F; text-align: center;">
                <p>Failed to load Caption Converter. Please try refreshing the page.</p>
            </div>
        `;
        throw error;
    }
}
/**
 * Destroy the Caption Converter instance and cleanup
 */
export function destroyCaptionConverter() {
    if (captionConverterRoot) {
        captionConverterRoot.unmount();
        captionConverterRoot = null;
        console.log('[RevWebflow] Caption Converter destroyed');
    }
}
// Export to global window object for Webflow usage
if (typeof window !== 'undefined') {
    window.RevWebflow = {
        ...(window.RevWebflow || {}),
        initCaptionConverter,
        destroyCaptionConverter,
    };
    // Per-bundle namespace so themes don't collide with sibling Webflow bundles
    // (voice recorder / audio trimmer) that have different theme shapes.
    window.RevWebflowCaptionConverterThemes = RevWebflowThemes;
}
